import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { AnalyticsClient } from './client';

/**
 * Static routes that travel verbatim into Mixpanel. Anything not in this
 * list and not matching the named patterns below is treated as a two-
 * segment dynamic path and normalized to `/.../:id` so we don't pollute
 * the property dictionary with one entry per market id.
 */
const STATIC_ROUTES = new Set(['/', '/wallet', '/settings', '/portfolio', '/dev/realtime']);

interface NamedPattern {
  test: RegExp;
  template: string;
}

const NAMED_PATTERNS: NamedPattern[] = [{ test: /^\/events\/[^/]+$/, template: '/events/:eventId' }];

/**
 * Normalize a router-style path to the bucket we track on. Strips
 * dynamic ids so the cardinality of `path` stays bounded.
 */
export function normalizePath(rawPath: string): string {
  if (!rawPath) return '/';
  const path = rawPath === '/' ? '/' : rawPath.replace(/\/+$/, '');

  if (STATIC_ROUTES.has(path)) return path;
  for (const pattern of NAMED_PATTERNS) {
    if (pattern.test.test(path)) return pattern.template;
  }

  // Unknown two-segment dynamic route — preserve structure, hide the id.
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 2) return `/${segments[0]}/:id`;

  return path;
}

/**
 * Mount once near the router. Fires `page_viewed` on every route change,
 * including the initial render.
 */
export function usePageViewTracking(client: AnalyticsClient): void {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = normalizePath(location.pathname);
    if (path === lastPath.current) return;
    const previous = lastPath.current ?? undefined;
    lastPath.current = path;
    client.track('page_viewed', {
      path,
      referrer: previous,
    });
  }, [location.pathname, client]);
}
